'use client';

import { boldMatchCharacters } from '@/general/helpers/boldMatchCharacters';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { AutocompleteItem } from 'src/general/components/Autocomplete/Autocomplete.types';
import { filterSidebarStore } from 'src/stores/filterSidebarStore';

import {
  FilterHierarchyFieldsGroupOption,
  FilterItemProps,
  FilterOption,
  PlpFiltersConditionalProps,
} from '../../PlpFilters.type';
import { FilterHierarchyFieldsGroup } from '../Fields/FilterHierarchyFieldsGroup';
import { FilterOptionFieldsGroup } from '../Fields/FilterOptionFieldsGroup';
import { flattenOptions, generateFuseIndex, getFilteredOptions } from './PlpFiltersMake.helper';
import styles from './PlpFiltersMake.module.scss';
import { PlpFiltersMakeLabelSelected } from './PlpFiltersMakeLabelSelected';

const Autocomplete = dynamic(
  () => import('src/general/components/Autocomplete/Autocomplete').then((mod) => mod.Autocomplete),
  { ssr: false },
);

type Props = {
  options: FilterHierarchyFieldsGroupOption[];
  currentFilters: FilterOption[];
  subCategoryLabel: string;
} & PlpFiltersConditionalProps;

export const PlpFiltersMake: FC<Props> = ({ options, currentFilters, subCategoryLabel, ...props }) => {
  const router = useRouter();
  const { displayMode, setDisplayMode } = filterSidebarStore(
    (state) => ({
      displayMode: state.displayMode,
      setDisplayMode: state.setDisplayMode,
    }),
    shallow,
  );
  const flattenedOptions: FilterItemProps[] = flattenOptions(options, currentFilters);

  const filteredOptions = getFilteredOptions(options, flattenedOptions, displayMode);

  const fuseIndex = generateFuseIndex(options);

  const getItems = async (value: string): Promise<AutocompleteItem<string>[]> => {
    const results = fuseIndex.search(value);
    const currentFilterValues = new Set(currentFilters.map((filter) => filter.value));

    const items: AutocompleteItem<string>[] = results
      .map(({ item, matches }) => ({
        ...item,
        key: `make-model-suggestion-${item.slug.toLowerCase()}`,
        label: (
          <PlpFiltersMakeLabelSelected
            label={boldMatchCharacters(`${item.label}`, matches)}
            typeLabel={item.typeLabel}
            testId={`make-model-suggestion-${item.slug.toLowerCase()}`}
          />
        ),
      }))
      .filter((item) => !currentFilterValues.has(item.value))
      // Limit the results to 5
      .splice(0, 5);
    return items;
  };

  const onSelectItem = (value: string) => {
    const selectedHierarchyFilterOption = options.find((option) =>
      option.children?.some((child) => child.value === value),
    );
    const selectedFilterOption = selectedHierarchyFilterOption
      ? selectedHierarchyFilterOption.children?.find((child) => child.value === value)
      : options.find((option) => option.value === value);
    if (selectedFilterOption) {
      if (props.type === 'sidebar') {
        router.push(selectedFilterOption.href ?? '');
      } else {
        props.onSelectFilter({
          filter: selectedFilterOption.filter,
          slug: selectedFilterOption.slug,
          value: selectedFilterOption.value,
        });
      }
    }
  };

  return (
    <div className={styles.root}>
      <Autocomplete
        id={`make-model-autocomplete`}
        name={'makeModelAutoComplete'}
        placeholder="Search makes, models"
        getItems={getItems}
        onSelectItem={onSelectItem}
        disablePortal={props.type === 'modal'}
        data-testid="make-model-autocomplete"
      />
      {/* Selected options */}
      {flattenedOptions.length > 0 && (
        <div className={styles.selectedOptions}>
          <FilterOptionFieldsGroup options={flattenedOptions} currentFilters={currentFilters} {...props} />
        </div>
      )}
      {/* Options list */}
      <p className={styles.listTitle}>{displayMode === 'popular' ? 'Popular makes' : 'All makes A-Z'}</p>
      <FilterHierarchyFieldsGroup
        options={filteredOptions}
        subCategoryLabel={subCategoryLabel}
        currentFilters={currentFilters}
        sortAlphabetically={displayMode === 'all'}
        {...props}
      />
      {/* Option display toggle */}
      <button
        className={styles.listSwitch}
        type="button"
        data-testid="PlpFiltersMake-displaySwitch"
        onClick={() => {
          setDisplayMode(displayMode === 'popular' ? 'all' : 'popular');
        }}
      >
        {displayMode === 'popular' ? 'See all makes' : 'See popular makes'}
      </button>
    </div>
  );
};
